import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, ClipboardList } from 'lucide-react';
import { warehouseAPI } from '../../api/warehouses';
import { supplierAPI } from '../../api/suppliers';
import { itemAPI } from '../../api/items';
import { WarehouseItemsTable, WarehouseItemRow } from '../../components/warehouse/WarehouseItemsTable';
import { Warehouse, Supplier, Item } from '../../types/entities';
import toast from 'react-hot-toast';

interface ManualReceiptFormData {
  warehouseId: number;
  transactionDate: string;
  supplierId?: number;
  transactionSubtype: string;
  notes?: string;
}

const SUBTYPES = [
  { value: 'opening_stock', labelKey: 'warehouse.manualReceipt.openingStock' },
  { value: 'direct_supplier', labelKey: 'warehouse.manualReceipt.directSupplier' },
  { value: 'adjustment', labelKey: 'warehouse.manualReceipt.adjustment' },
  { value: 'other', labelKey: 'warehouse.manualReceipt.other' },
];

export const ManualReceiptPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const warehouseIdParam = searchParams.get('warehouseId');

  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [catalogItems, setCatalogItems] = useState<Item[]>([]);
  const [items, setItems] = useState<WarehouseItemRow[]>([
    { id: 'temp-1', itemId: null, itemName: '', quantity: 0, unit: 'M3' },
  ]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ManualReceiptFormData>({
    defaultValues: {
      warehouseId: warehouseIdParam ? parseInt(warehouseIdParam) : undefined,
      transactionDate: new Date().toISOString().split('T')[0],
      transactionSubtype: 'other',
    },
  });

  const selectedSubtype = watch('transactionSubtype');
  const selectedWarehouseId = watch('warehouseId');

  useEffect(() => {
    loadWarehouses();
    loadSuppliers();
    loadCatalogItems();
  }, []);

  const loadWarehouses = async () => {
    try {
      const data = await warehouseAPI.getAll({ status: 'ACTIVE' });
      setWarehouses(data);
    } catch {
      toast.error(t('warehouses.loadError'));
    }
  };

  const loadSuppliers = async () => {
    try {
      const data = await supplierAPI.getAll();
      setSuppliers(data.filter((s) => s.status === 'ACTIVE'));
    } catch {
      toast.error(t('suppliers.loadError'));
    }
  };

  const loadCatalogItems = async () => {
    try {
      const data = await itemAPI.getAll();
      setCatalogItems(data.filter((i) => i.status === 'ACTIVE'));
    } catch {
      toast.error(t('items.loadError'));
    }
  };

  const onSubmit = async (data: ManualReceiptFormData) => {
    if (items.length === 0) {
      toast.error(t('warehouse.manualReceipt.itemsRequired'));
      return;
    }

    const hasInvalidItems = items.some((item) => !item.itemId || item.quantity <= 0);
    if (hasInvalidItems) {
      toast.error(t('warehouse.manualReceipt.invalidItems'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        transactionDate: data.transactionDate,
        supplierId: data.supplierId || undefined,
        transactionSubtype: data.transactionSubtype,
        notes: data.notes || undefined,
        items: items.map((item) => ({
          itemId: item.itemId!,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
        })),
      };

      await warehouseAPI.manualReceipt(data.warehouseId, payload);
      toast.success(t('warehouse.manualReceipt.success'));
      navigate(`/warehouses/${data.warehouseId}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('warehouse.manualReceipt.title')}
          </h1>
          <p className="text-gray-500 text-sm mt-1">{t('warehouse.manualReceipt.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedWarehouseId && (
            <Link
              to={`/warehouses/audit-logs?warehouseId=${selectedWarehouseId}`}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <ClipboardList className="w-4 h-4" />
              {t('nav.auditLogs')}
            </Link>
          )}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </button>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Section: Thông tin cơ bản */}
        <div className="card space-y-5">
          <h2 className="text-base font-semibold text-gray-800 border-b pb-3">
            {t('warehouse.manualReceipt.basicInfo')}
          </h2>

          {/* Row 1: Kho + Ngày nhập */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.manualReceipt.warehouse')} <span className="text-red-500">*</span>
              </label>
              <select
                {...register('warehouseId', { required: true, valueAsNumber: true })}
                className={`input-field w-full ${errors.warehouseId ? 'border-red-300' : ''}`}
              >
                <option value="">{t('warehouse.manualReceipt.selectWarehouse')}</option>
                {warehouses.map((wh) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.name} ({wh.code})
                  </option>
                ))}
              </select>
              {errors.warehouseId && (
                <p className="text-red-500 text-xs mt-1">
                  {t('warehouse.manualReceipt.warehouseRequired')}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.manualReceipt.transactionDate')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('transactionDate', { required: true })}
                className={`input-field w-full ${errors.transactionDate ? 'border-red-300' : ''}`}
              />
            </div>
          </div>

          {/* Loại giao dịch */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('warehouse.manualReceipt.transactionSubtype')} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {SUBTYPES.map(({ value, labelKey }) => (
                <label
                  key={value}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm ${
                    selectedSubtype === value
                      ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    {...register('transactionSubtype')}
                    value={value}
                    className="sr-only"
                  />
                  <span
                    className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${
                      selectedSubtype === value
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300'
                    }`}
                  />
                  {t(labelKey)}
                </label>
              ))}
            </div>
          </div>

          {/* Nhà cung cấp (chỉ hiện khi direct_supplier) */}
          {selectedSubtype === 'direct_supplier' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('warehouse.manualReceipt.supplier')}
              </label>
              <select
                {...register('supplierId', { valueAsNumber: true })}
                className="input-field w-full"
              >
                <option value="">{t('warehouse.manualReceipt.supplierPlaceholder')}</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Section: Danh sách hàng hóa */}
        <div className="card space-y-4">
          <h2 className="text-base font-semibold text-gray-800 border-b pb-3">
            {t('warehouse.manualReceipt.items')} <span className="text-red-500">*</span>
          </h2>
          <WarehouseItemsTable
            items={items}
            onChange={setItems}
            catalogItems={catalogItems}
          />
        </div>

        {/* Section: Ghi chú */}
        <div className="card">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('warehouse.manualReceipt.notes')}
          </label>
          <textarea
            {...register('notes')}
            rows={3}
            className="input-field w-full"
            placeholder={t('warehouse.manualReceipt.notesPlaceholder')}
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn-primary flex items-center gap-2"
            disabled={loading}
          >
            <Save className="w-4 h-4" />
            {loading ? t('common.loading') : t('warehouse.manualReceipt.submit')}
          </button>
        </div>
      </form>
    </div>
  );
};
